/*
 * $Id:: ApplicationFilters.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ReactComponent as ArrowDown } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUp } from '../../assets/images/arrow_up.svg';
import { ReactComponent as InfoCloseIcon } from '../../assets/images/info_close_icon_blue.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info_icon.svg';
import InfoMessage from '../../common/components/InfoMessage';
import { t } from '../../providers';
import SearchFilter from '../SearchFilter';
import DropdownFilters from './DropdownFilters';
import FilterActionButtons from './FilterActionButtons';

const useStyles = makeStyles((theme) => ({
    actionsContainer: {
        display: 'flex',
        flexFlow: 'column nowrap',
        width: '100%',
        marginBottom: '40px',
        '@media (max-width:600px)': {
            display: 'block',
            flexFlow: 'unset',
        },
    },
    searchRow: {
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        margin: '0px 0px 10px 0px',
        '@media (max-width:600px)': {
            display: 'block',
            flexFlow: 'unset',
            width: '100%',
        },
    },
    searchRowExport: {
        marginLeft: '0px',
        '@media (max-width:600px)': {
            marginLeft: '0px',
        },
    },
    filtersRow: {
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        margin: '10px 0px 5px 0px',
    },
    filterTextRow: {
        width: '85%',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        '@media (max-width:600px)': {
            paddingBottom: '20px',
        },
    },
    toggleFilters: {
        color: theme.palette.text.mainGreen,
        fontSize: '0.875rem',
        padding: '10px 0px',
        fontWeight: '600',
        justifyContent: 'flex-start',
        '&:hover': {
            cursor: 'pointer',
        },
        '& svg': {
            paddingLeft: '10px',
        },
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    searchButton: {
        height: '32px',
        minWidth: '4.5rem',
        color: theme.palette.text.mainGreen,
        border: '1px solid' + theme.palette.text.mainGreen,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: '2px',
        lineHeight: '12px',
    },
}));

const ApplicationFilters = ({
    applicationType,
    filtersValues,
    setFiltersValues,
    clearFilters,
    _initiateServices,
    exportApplications,
    filterItems,
    datesOptions = 'dates',
    isTablet,
    hideInfo,
    toggleInfoMessage,
    handleDateFilter,
    expoTypes,
    exportError,
    exportLoading,
    page,
    getUserList = () => {},
    searchLabel = 'search.in.application.text',
    setGetFullListIsPressed = () => {},
    showFilters = true,
    showGetFullListButton = true,
    noDataToExport = false,
}) => {
    const classes = useStyles();

    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({});
    const [filtersChips, setFiltersChips] = useState({});
    const [hideFilters, setHideFilters] = useState(false);

    useEffect(() => {
        setSearchQuery(filtersValues.searchQuery);
        setFilters(filtersValues.filters);
        setFiltersChips(filtersValues.filtersChips);
    }, [filtersValues]);

    const handleUpdateFilters = (filtersObject, optionSelected) => {
        for (let key in filtersObject) {
            // eServices filters: clean formTypes filter if ipRightType is changed
            if (key === 'ipRightType' && filtersValues.filters[key] !== filtersObject[key] && filtersObject['formTypes']) {
                delete filtersObject['formTypes'];
                delete optionSelected['formTypes'];
            }
            //remove filters with no options selected from active filters
            if (filtersObject[key].length <= 0 && key !== 'dates') {
                delete filtersObject[key];
                delete optionSelected[key];
            }
        }
        setFiltersValues({ ...filtersValues, filters: filtersObject, filtersChips: optionSelected });
    };

    const search = (query) => {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length > 2) {
            setFiltersValues({ ...filtersValues, searchQuery: query });
        }
    };

    const clearSearch = () => {
        if (page === 'impersonation') {
            setGetFullListIsPressed(true);
        }
        setFiltersValues({ ...filtersValues, searchQuery: '' });
    };

    const exportTypes = () => {
        return expoTypes.map((type) => ({ label: type.toUpperCase(), action: () => exportApplications(type) }));
    };

    const toggleFilters = () => {
        setHideFilters(!hideFilters);
    };

    const selectedService = applicationType === 'patents' ? applicationType : `${applicationType}s`;

    return (
        <div className={classes.actionsContainer}>
            {!hideInfo && (
                <div className={classes.searchRow}>
                    <InfoMessage
                        infoIcon={InfoIcon}
                        title={t('info.title.text')}
                        subtitle={t(`${page}.${selectedService}.info.text`)}
                        closeIcon={InfoCloseIcon}
                        infoStyle={{
                            marginTop: '-14px',
                            background: '#E0F4FD',
                        }}
                        action={() => {
                            toggleInfoMessage(true);
                        }}
                    />
                </div>
            )}

            <div className={classes.searchRow} role="search">
                <SearchFilter
                    onSearch={search}
                    page={page}
                    searchQuery={searchQuery}
                    getUserList={getUserList}
                    onClear={clearSearch}
                    toggleInfoMessage={toggleInfoMessage}
                    searchLabel={searchLabel}
                    showGetFullListButton={showGetFullListButton}
                    hideInfo={hideInfo}
                />
                {!isTablet && (
                    <FilterActionButtons
                        filterItems={filterItems}
                        applicationType={applicationType}
                        exportTypes={exportTypes}
                        exportError={exportError}
                        exportLoading={exportLoading}
                        noDataToExport={noDataToExport}
                    />
                )}
            </div>

            {isTablet && showFilters && (
                <Button
                    className={classes.toggleFilters}
                    onClick={toggleFilters}
                    disableFocusRipple
                    aria-controls="filters-section"
                    aria-expanded={!hideFilters}
                    id="accordion-toggle-filters">
                    {t('show.filter.options')}
                    {hideFilters ? <ArrowUp aria-hidden="true" focusable="false" /> : <ArrowDown aria-hidden="true" focusable="false" />}
                </Button>
            )}

            {!hideFilters && showFilters && (
                <DropdownFilters
                    filterItems={filterItems}
                    onUpdateFilters={handleUpdateFilters}
                    filters={filters}
                    applicationType={applicationType}
                    datesOptions={datesOptions}
                    filtersChips={filtersChips}
                    handleLastFilter={clearFilters}
                    handleDateFilter={handleDateFilter}
                    page={page}
                    id="filters-section"
                    ariaHidden={hideFilters}
                />
            )}

            <div className={classes.searchRowExport}>
                {isTablet && page !== 'impersonation' && (
                    <FilterActionButtons
                        filterItems={filterItems}
                        applicationType={applicationType}
                        exportTypes={exportTypes}
                        exportError={exportError}
                        exportLoading={exportLoading}
                        noDataToExport={noDataToExport}
                    />
                )}
            </div>
        </div>
    );
};

export default ApplicationFilters;
